import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { usePublicLanguage } from "../../context/PublicLanguage";
import Logo from "@/components/Logo";

const DESKTOP_ID = { Cybersecurity: "Siber", "Future Tech": "Teknologi" };
const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/ai", label: "AI" },
  { to: "/robotics", label: "Robotics" },
  { to: "/cybersecurity", label: "Cybersecurity" },
  { to: "/future-tech", label: "Future Tech" },
  { to: "/business", label: "Business" },
  { to: "/guides", label: "Guides" },
  { to: "/daily-dish", label: "Daily Dish" },
];

export default function Navbar() {
  const { t, language } = usePublicLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setQ("");
      setMobileOpen(false);
    }
  };

  const linkClass = ({ isActive }) =>
    `td-eyebrow whitespace-nowrap py-1.5 rounded transition-colors duration-200 ${
      isActive ? "text-[#FF8A1F]" : "text-[#A7ABB0] hover:text-white"
    }`;

  return (
    <header
      className="sticky top-0 z-50 border-b transition-colors duration-300"
      style={{
        borderColor: "rgba(255,255,255,0.08)",
        background: scrolled ? "rgba(8,9,10,0.82)" : "rgba(8,9,10,0.6)",
        backdropFilter: "blur(18px)",
      }}
    >
      <div className="mx-auto max-w-[1320px] px-5 md:px-8">
        <div className="flex items-center justify-between h-[68px] gap-2">
          <Logo iconSize={36} showTagline className="public-navbar-brand" />

          <nav className="hidden xl:flex items-center gap-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkClass} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                {language === "id" ? DESKTOP_ID[n.label] || t(n.label) : n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <form onSubmit={submitSearch} className="hidden md:flex items-center relative">
              <Search size={15} className="absolute left-3 text-[#71767c]" />
              <input
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("Search")} aria-label={t("Search")}
                className="w-28 2xl:w-32 bg-[#111315] border rounded-full pl-9 pr-3 py-2 text-sm text-white placeholder:text-[#71767c] outline-none focus:border-[#FF8A1F]/50"
                style={{ borderColor: "rgba(255,255,255,0.08)", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem" }}
              />
            </form>
            <button
              className="xl:hidden p-2 text-white"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label={t("Menu")} aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden border-t td-fade-up" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#08090A" }}>
          <div className="px-5 py-4">
            <form onSubmit={submitSearch} className="flex items-center relative mb-4">
              <Search size={16} className="absolute left-3 text-[#71767c]" />
              <input
                data-testid="mobile-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("Search TechDishes")} aria-label={t("Search TechDishes")}
                className="w-full bg-[#111315] border rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-[#71767c] outline-none focus:border-[#FF8A1F]/50"
                style={{ borderColor: "rgba(255,255,255,0.08)" }}
              />
            </form>
            <div className="grid grid-cols-2 gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `td-eyebrow py-3 px-2 rounded ${isActive ? "text-[#FF8A1F] bg-[#111315]" : "text-[#A7ABB0]"}`
                  }
                >
                  {t(n.label)}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
